/**
 * SKYPULSE — every gameplay magic number lives here.
 *
 * Tune feel by editing this one file. Anything that changes simulation
 * behaviour bumps TUNING_VERSION (replays/ghosts embed it; a mismatch means
 * an old input log may no longer reproduce the same run).
 */

/** R2: bumped to 3 — silently invalidates all stored ghosts (per D-026;
 *  intended, see Round 2 brief §0.2). */
export const TUNING_VERSION = 3;

// ---------------------------------------------------------------------------
// Simulation clock
// ---------------------------------------------------------------------------
/** Fixed simulation rate (Hz). The renderer interpolates between sim states. */
export const SIM_HZ = 120;
/** Fixed timestep in seconds. */
export const SIM_DT = 1 / SIM_HZ;

/** Convert a milliseconds window into whole sim ticks (round to nearest). */
export function msToTicks(ms: number): number {
  return Math.round((ms / 1000) * SIM_HZ);
}

// ---------------------------------------------------------------------------
// Track geometry
// ---------------------------------------------------------------------------
/** Number of lanes across the road. */
export const LANES = 7;
/** Width of one cell in world units (cells are CELL_WIDTH × ROW_DEPTH). */
export const CELL_WIDTH = 2;
/** Depth of one row in world units. */
export const ROW_DEPTH = 2;
/** Height of one block level in world units (block '1'..'3' → 1..3 units). */
export const BLOCK_UNIT_HEIGHT = 1;
/** Tunnel roof height above the tunnel floor. */
export const TUNNEL_ROOF_HEIGHT = 2.0;

/** X coordinate of a lane center. Lane 3 (middle of 7) is x = 0. */
export function laneCenterX(lane: number): number {
  return (lane - (LANES - 1) / 2) * CELL_WIDTH;
}

// ---------------------------------------------------------------------------
// Ship kinematics — steering
// ---------------------------------------------------------------------------
/** Max lateral speed, units/s. */
export const STEER_MAX_SPEED = 14;
/** Time to reach max lateral speed, ms (accel = max/ramp). Snappy but not twitchy. */
export const STEER_RAMP_MS = 60;
/** Lateral clamp: ship center may go one half-lane beyond the outer lane edge. */
export const X_LIMIT = ((LANES - 1) / 2) * CELL_WIDTH + CELL_WIDTH / 2;

// ---------------------------------------------------------------------------
// Throttle (R2 Feature A — the original's defining verb)
// ---------------------------------------------------------------------------
export const THROTTLE_MIN = 0.6;
export const THROTTLE_MAX = 1.4;
/** Ramp rate per second while accelerate/brake is held (inertia, not a switch). */
export const THROTTLE_RAMP_PER_S = 0.35;
/** Releasing input within ± this of 1.0 snaps to exactly 1.0 (musical home). */
export const THROTTLE_DETENT_BAND = 0.02;
/** effRate = clamp(throttle × padFactor, …) — the master rate for songTime. */
export const EFFRATE_MIN = 0.5;
export const EFFRATE_MAX = 1.6;

// ---------------------------------------------------------------------------
// Gravity presets (R2 §1.3): jump airtime in BEATS at throttle 1.0.
// Apex scales with airtime so arcs keep their proportions.
// ---------------------------------------------------------------------------
export type GravityPreset = "floaty" | "normal" | "heavy";
export const GRAVITY_PRESETS: Record<GravityPreset, { airtimeBeats: number; apex: number }> = {
  floaty: { airtimeBeats: 2, apex: 2.6 },
  normal: { airtimeBeats: 1, apex: 1.6 },
  heavy: { airtimeBeats: 0.5, apex: 1.0 },
};

// ---------------------------------------------------------------------------
// Ship kinematics — jump (PILLAR A: a jump IS a musical gesture)
// ---------------------------------------------------------------------------
/**
 * Airtime is defined by the level's gravity preset in BEATS at throttle 1.0
 * (R2 §1.3, amending D-010) and is fixed in REAL time per preset+BPM. Since
 * forward speed scales with effRate while airtime does not, jump length in
 * rows ≈ rowsPerBeat × airtimeBeats × effRate — full throttle stretches jumps
 * ~40 % farther. That is the original's core skill (R2 §1.4), by design.
 */
export interface JumpParams {
  airtimeS: number;
  gravity: number;
  jumpV0: number;
}

/** Derive the jump parabola from song BPM + gravity preset (g = 8·h/t², v0 = g·t/2). */
export function jumpParamsForBpm(bpm: number, preset: GravityPreset = "normal"): JumpParams {
  const { airtimeBeats, apex } = GRAVITY_PRESETS[preset];
  const airtimeS = (60 / bpm) * airtimeBeats;
  const gravity = (8 * apex) / (airtimeS * airtimeS);
  return { airtimeS, gravity, jumpV0: (gravity * airtimeS) / 2 };
}

// ---------------------------------------------------------------------------
// Feel windows (non-negotiable per spec)
// ---------------------------------------------------------------------------
/** Jump accepted this long after leaving a ledge (ms). */
export const COYOTE_MS = 90;
/** Jump pressed this long before landing executes on landing (ms). */
export const JUMP_BUFFER_MS = 120;
/** Edge-landing forgiveness: fraction of cell width added to support overlap. */
export const LANDING_TOLERANCE_FRAC = 0.15;

export const COYOTE_TICKS = msToTicks(COYOTE_MS);
export const JUMP_BUFFER_TICKS = msToTicks(JUMP_BUFFER_MS);
export const LANDING_TOLERANCE = LANDING_TOLERANCE_FRAC * CELL_WIDTH;

// ---------------------------------------------------------------------------
// Collision hitboxes
// ---------------------------------------------------------------------------
/**
 * Support half-width: how far the ship center may hang off a cell and still
 * be supported = physical half-width + landing tolerance. Generous — deaths
 * must feel earned.
 */
export const SHIP_HALF_WIDTH = 0.55;
export const SUPPORT_HALF_WIDTH = SHIP_HALF_WIDTH + LANDING_TOLERANCE;
/** Deadly-contact half-width (frontal hits, hazards). Tighter than visual. */
export const HIT_HALF_WIDTH = 0.45;
/** Nose lookahead for frontal collision, world units ahead of ship center. */
export const NOSE_LENGTH = 0.6;
/** A cell top this much above ship y blocks (frontal death); below = walkable step. */
export const STEP_TOLERANCE = 0.25;
/** Ship dies when falling below this y. */
export const FALL_DEATH_Y = -3;
/** Hazard cells kill when ship y is below this height above the cell floor. */
export const HAZARD_KILL_HEIGHT = 0.8;
/**
 * R2 §2.1 (repeals R1 "jump in tunnel = death"): the ship's TOP reaching the
 * roof head-bumps (vy → 0, fall begins) — never fatal by itself. Entrance
 * rims stay deadly: entering a roofed row above this clearance = frontal.
 */
/** 0.55 makes `normal` jumps bump decisively (discrete apex ≈ 1.55 vs
 *  ceiling 1.45), `floaty` bump hard, and `heavy` hops (apex 1.0) run free —
 *  matching the original's tunnel feel. */
export const SHIP_TOP_CLEARANCE = 0.55;
/** Grounded ship may follow a surface dropping this much per tick (ramps). */
export const STEP_DOWN_TOLERANCE = 0.15;

/**
 * Capped-arc airtime under a roof (R2 §2.2 validator math): rise stops at
 * maxRise (roof − clearance), then free fall from there.
 */
export function cappedAirtimeS(jump: JumpParams, maxRise: number): number {
  const { jumpV0: v0, gravity: g } = jump;
  const fullApex = (v0 * v0) / (2 * g);
  if (maxRise >= fullApex) return jump.airtimeS; // roof never touched
  const tRise = (v0 - Math.sqrt(v0 * v0 - 2 * g * maxRise)) / g;
  const tFall = Math.sqrt((2 * maxRise) / g);
  return tRise + tFall;
}

// ---------------------------------------------------------------------------
// Ice (R2 Feature C): the ship drifts.
// ---------------------------------------------------------------------------
/** Lateral acceleration multiplier while grounded on ice. */
export const ICE_LAT_ACCEL = 0.45;
/** Lateral damping multiplier (steer released) on ice. */
export const ICE_LAT_DAMP = 0.25;
/** Bits collect when ship y below this and laterally overlapping. */
export const BIT_COLLECT_HEIGHT = 1.4;

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------
export const CAM_BACK = 9; // distance behind ship (z)
export const CAM_UP = 4.2; // height above track floor
export const CAM_AHEAD = 8; // look-at point this far ahead of ship
export const CAM_STIFFNESS = 8; // spring: pos = lerp(pos, target, 1 - e^(-k·dt))
export const CAM_X_FOLLOW = 0.55; // fraction of ship x the camera copies (parallax feel)
export const FOV_BASE_DEG = 60;

// ---------------------------------------------------------------------------
// Rendering / track window
// ---------------------------------------------------------------------------
/** Max rows ahead (High tier; buffers are sized for this, tiers shrink it). */
export const WINDOW_ROWS_AHEAD = 40;
export const WINDOW_ROWS_BEHIND = 6;

// ---------------------------------------------------------------------------
// Polish (M4): on-beat bonus, death feedback, shake, trail
// ---------------------------------------------------------------------------
/** Jumping within ± this of a beat shows "ON BEAT!" (spec 5.3 — additive,
 *  never required to survive). */
export const ON_BEAT_WINDOW_MS = 60;
/** Screen shake on death / boost pickup, ms (≤ 120 per spec 3.4). */
export const SHAKE_MS = 120;
export const SHAKE_AMPLITUDE = 0.14;
/** Death music low-pass sweep target (Hz) and open state. */
export const DEATH_LOWPASS_HZ = 380;
export const OPEN_LOWPASS_HZ = 19000;
/** Ship trail (High/Med tiers). */
export const TRAIL_LENGTH = 24;
export const TRAIL_DIAMETER = 0.5;

// ---------------------------------------------------------------------------
// Speed pads (R2 §1.5, supersedes D-019): multiplicative RATE modifiers.
// They modulate effRate — so they audibly pitch the music, like a tape.
// ---------------------------------------------------------------------------
/** Boost pad rate multiplier. */
export const PAD_BOOST_FACTOR = 1.25;
/** Sticky pad rate multiplier. */
export const PAD_STICKY_FACTOR = 0.75;
/** How long a pad's effect lasts, in SONG beats from grounded touch. */
export const PAD_DURATION_BEATS = 4;
/** FOV maps 58°→70° over effRate 0.6→1.6 (R2 VFX-1), smoothed ~200 ms. */
export const FOV_MIN_DEG = 58;
export const FOV_MAX_DEG = 70;
export const FOV_SMOOTH_S = 0.2;

// ---------------------------------------------------------------------------
// Medals v2 (R2 §1.6, supersedes D-018): authored again. The ladder derives
// from the developer's recorded best (the authoring ghost).
// ---------------------------------------------------------------------------
export const MEDAL_LADDER = { gold: 1.07, silver: 1.18, bronze: 1.35 } as const;

// ---------------------------------------------------------------------------
// Checkpoints, practice & count-in (Pillar B, spec 6.2)
// ---------------------------------------------------------------------------
/** Musical pre-roll before control at a checkpoint respawn (kept short —
 *  death→control must stay < 1.5 s). */
export const RESPAWN_PREROLL_BEATS = 1;
/** Count-in before control when practicing from a section (spec 6.2). */
export const PRACTICE_COUNT_IN_BEATS = 4;

// ---------------------------------------------------------------------------
// Beat-reactive presentation (PILLAR A visuals)
// ---------------------------------------------------------------------------
/** Beat pulse curve: pulse = e^(−beatPhase × decay). Higher = snappier spike. */
export const BEAT_PULSE_DECAY = 5.5;
/** Neon edge emissive = base × (NEON_DIM + NEON_GAIN × pulse). */
export const NEON_DIM = 0.65;
export const NEON_GAIN = 0.9;
/** Hazards flash on-beat (spec 5.3). */
export const HAZARD_DIM = 0.7;
export const HAZARD_GAIN = 0.65;
/** Beat-pulse horizon ring — the signature element (spec Section 7).
 *  Sized to frame the road's vanishing point (~±14° at this distance/FOV). */
export const RING_DISTANCE = 95; // ahead of the camera
export const RING_HEIGHT = 2.5;
export const RING_DIAMETER = 46;
export const RING_SCALE_KICK = 0.06; // scale grows this much on each beat
/** Reduced motion: pulses are replaced by a slow glow at this rate (Hz ≤ 0.5). */
export const REDUCED_MOTION_GLOW_HZ = 0.4;

// ---------------------------------------------------------------------------
// Juice & sky (R2-M4, brief §5/§6)
// ---------------------------------------------------------------------------
/** Speed-lines fade in above this effRate, full at EFFRATE_MAX (VFX-1). */
export const SPEEDLINE_MIN_EFFRATE = 1.1;
/** Landing impact FX fires for drops of at least this height (VFX-2). */
export const LANDING_IMPACT_MIN_DROP = 0.5;
/** Death desaturation ripple duration (VFX-7) — a single sub-3 Hz event. */
export const DEATH_RIPPLE_MS = 250;
/** Finish camera orbit: total sweep and angular speed (VFX-5). */
export const ORBIT_TOTAL_DEG = 90;
export const ORBIT_DEG_PER_S = 36;
/** Roadside pylons: cadence in rows, lateral offset (§6.3). */
export const DECO_PYLON_EVERY_ROWS = 8;
export const DECO_PYLON_X = 10.5;
/** Far ring structures: cadence in rows, lateral offset (§6.3). */
export const DECO_RING_EVERY_ROWS = 48;
export const DECO_RING_X = 34;
/** Tunnel dressing (§2.3): rib cadence and the 3-phase traveling wave. */
export const TUNNEL_RIB_EVERY_ROWS = 2;
/** Near-miss whoosh: min effRate and per-event cooldown in beats (P1). */
export const NEARMISS_MIN_EFFRATE = 1.15;
export const NEARMISS_COOLDOWN_BEATS = 1;

// ---------------------------------------------------------------------------
// Touch v3 (D-055): pseudo-joystick + touchdown jump + broad throttle strip
// ---------------------------------------------------------------------------
/** Horizontal thumb offset (px) for full steer deflection. Absolute pixels —
 *  thumb travel is a hand property, not a screen property. */
export const TOUCH_JOY_FULL_PX = 80;
/** Throttle strip grab zone: fraction of width, clamped to [min,max] px so
 *  it stays broad on phones without eating the jump half on tablets. */
export const TOUCH_SLIDER_FRAC = 0.22;
export const TOUCH_SLIDER_MIN_PX = 84;
export const TOUCH_SLIDER_MAX_PX = 220;

// ---------------------------------------------------------------------------
// Audio
// ---------------------------------------------------------------------------
/** Look-ahead scheduling (the "Tale of Two Clocks" pattern, spec 5.1). */
export const SCHEDULER_INTERVAL_MS = 25;
export const SCHEDULER_HORIZON_S = 0.1;
/** Source start is scheduled this far ahead for sample-accurate alignment. */
export const SONG_START_DELAY_S = 0.08;
/** Calibration: tap-offset clamp (spec 5.4). */
export const CALIBRATION_CLAMP_MS = 250;
export const CALIBRATION_BEATS = 16; // scored taps
export const CALIBRATION_COUNT_IN = 4; // unscored lead-in beats
export const CALIBRATION_BPM = 120;

// ---------------------------------------------------------------------------
// Death & retry loop
// ---------------------------------------------------------------------------
/** Delay from death event to the retry overlay appearing (ms, render-side only). */
export const DEATH_OVERLAY_DELAY_MS = 300;

// ---------------------------------------------------------------------------
// Palettes (hex per spec Section 7 — never introduce colors outside a
// world's 5-value palette; bits are always warm white)
// ---------------------------------------------------------------------------
/** R2 §6.4: each world = 5 surface colors + exactly 2 named sky tones
 *  (never exceed 7); hazard red and bit warm-white stay global constants. */
export interface WorldPalette {
  spaceBase: string;
  road: string;
  neon: string;
  accent: string;
  hazard: string;
  bit: string;
  skyPrimary: string;
  skySecondary: string;
}

export const PALETTE_W1: WorldPalette = {
  spaceBase: "#07070F",
  road: "#101024",
  neon: "#22D3EE", // cyan
  accent: "#F472B6", // magenta
  hazard: "#FF3355",
  bit: "#FFF7D6",
  skyPrimary: "#0A1B2E", // deep coastal blue
  skySecondary: "#251331", // magenta dusk
};

export const PALETTE_W2: WorldPalette = {
  spaceBase: "#07070F",
  road: "#101024",
  neon: "#FB923C", // amber
  accent: "#A78BFA", // violet
  hazard: "#FF3355",
  bit: "#FFF7D6",
  skyPrimary: "#2E0D12", // dim red
  skySecondary: "#33200F", // ember dusk
};

/** World 3 "Duskfall Cascade" (R2 §4.1). */
export const PALETTE_W3: WorldPalette = {
  spaceBase: "#07070F",
  road: "#0E1420",
  neon: "#34F5C5", // mint
  accent: "#3B82F6", // electric blue
  hazard: "#FF3355",
  bit: "#FFF7D6",
  skyPrimary: "#0B2530", // deep teal
  skySecondary: "#2A1B3D", // dusk violet
};

export const PALETTES: Record<string, WorldPalette> = {
  world1: PALETTE_W1,
  world2: PALETTE_W2,
  world3: PALETTE_W3,
};
